import axios from "axios";
import React, { useEffect } from "react";
import { useState, useContext } from "react";
import { Container, Button, Card, Modal } from "react-bootstrap";
import {
  useLocation,
  useOutletContext,
  useParams,
  useNavigate,
} from "react-router";
import Question from "./Question";
import { ApiConfigContext } from "context/ApiConfig/ApiConfigContext";
import Loading from "components/Loading";
import api from "utils/axiosInstance";
import { UserContext } from "context/User/UserContext";
import { Link } from "react-router-dom";

function WelcomeComponent({ moduleNumber, startAssessment }) {
  const navigate = useNavigate();

  return (
    <Container className="text-center d-flex flex-column justify-content-center align-items-center vh-100">
      <h1>Welcome to the the Module {moduleNumber} quiz! </h1>
      <h2>
        Before you go to the next module, you are required to take this quiz
      </h2>
      <Button variant="primary" className="mt-3" onClick={startAssessment}>
        Get Started
      </Button>
      <Button
        variant="secondary"
        className="mt-3"
        onClick={() => navigate(process.env.PUBLIC_URL + "/subject")}
      >
        Go Back
      </Button>
    </Container>
  );
}

function QuizCompleteComponent({ message, prevWeek }) {
  const navigate = useNavigate();
  return (
    <Container className="text-center d-flex flex-column justify-content-center align-items-center vh-100">
      <h1>{message}</h1>
      <Button
        variant="primary"
        className="mt-3"
        as={Link}
        to={process.env.PUBLIC_URL + `/subject/quiz/report/${prevWeek}`}
      >
        View Report
      </Button>
      <Button variant="primary" className="mt-3" onClick={() => navigate(-1)}>
        Continue
      </Button>
    </Container>
  );
}

export function Questionnaire() {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState(null);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [questionnaireChoices, setQuestionnaireChoices] = useState([]);
  const [toBeAnswered, setToBeAnswered] = useState([]);
  const [isQuizComplete, setIsQuizComplete] = useState(false);
  const [error, setError] = useState({ state: false, message: "" });
  const [warning, setWarning] = useState({ state: false, message: "" });

  const [quizCompleteMessage, setQuizCompleteMessage] = useState(
    "You have already completed the quiz"
  );
  const { routes } = useContext(ApiConfigContext);
  const { user } = useContext(UserContext);
  const { prevWeek } = useOutletContext();

  useEffect(() => {
    (async function () {
      var isQuizComplete = await getQuizCompleteStatus(user.username, prevWeek);
      setIsQuizComplete(isQuizComplete);
      const questions = await getQuestions();
      setQuestions(questions);
      initQuizChoices(questions.length);
      setLoading(false);
      setShowWelcomeScreen(true);
    })();
  }, []);

  async function getQuizCompleteStatus(subjectId, weekNumber) {
    var quizCompleteStatus = null;
    try {
      quizCompleteStatus = await api({
        url: `/edu/module/${subjectId}/quiz/${weekNumber}/complete`,
        method: "get",
      });
      return quizCompleteStatus.data;
    } catch (error) {
      console.log(error);
      return null;
    }
  }
  const initQuizChoices = (quizLength) => {
    const initialChoices = [];
    for (let quizId = 0; quizId < quizLength; quizId++)
      initialChoices.push({ number: quizId, choice: null });
    setQuestionnaireChoices(initialChoices);
  };

  const getQuestions = async () => {
    const apiResponse = await api({
      method: "get",
      url: `/edu/module/quiz/${user.username}/${prevWeek}`,
    });
    const responseData = apiResponse.data;

    return responseData.questions.map((q) => {
      return {
        questionId: q.questionId,
        questionTitle: q.questionText,
        choices: [q.option1, q.option2, q.option3, q.option4],
      };
    });
  };

  const startAssessment = () => {
    setShowWelcomeScreen(false);
  };

  const markAnswered = (questionIdx) => {
    setToBeAnswered((prevState) => prevState.filter((q) => q !== questionIdx));
  };

  const saveSelection = (currQuestionNumber, newChoice) => {
    if (newChoice == null) {
      return;
    }
    markAnswered(currQuestionNumber);
    setQuestionnaireChoices((questionnaireChoices) => [
      ...questionnaireChoices.filter(
        (choice) => choice.number !== currQuestionNumber
      ),
      {
        number: currQuestionNumber,
        choice: newChoice,
      },
    ]);
  };

  const getUnansweredQuestions = () => {
    return questionnaireChoices
      .filter((question) => question.choice === null)
      .map((question) => question.number);
  };

  const submitQuestionnaire = async () => {
    const unansweredQuestions = getUnansweredQuestions();
    if (Array.isArray(unansweredQuestions) && unansweredQuestions.length > 0) {
      setWarning({
        state: true,
        message: "Please complete all questions before submitting",
      });
      setToBeAnswered([...unansweredQuestions]);
      return;
    }

    const postQuestionnaire = questionnaireChoices.map((q) =>
      api({
        method: "post",
        url: `/edu/module/quiz/${prevWeek}`,
        data: {
          userId: user.username,
          quizId: prevWeek,
          questionId: q.number + 1,
          selectedOption: q.choice + 1,
        },
      })
    );
    try {
      const quizPostResponse = await Promise.all(postQuestionnaire);

      setIsQuizComplete(true);
      setQuizCompleteMessage(`You have successfully completed the quiz.`);
    } catch (err) {
      alert("Failed to submit the quiz");
    }
  };

  if (loading) return <Loading />;

  if (isQuizComplete) {
    return (
      <QuizCompleteComponent
        message={quizCompleteMessage}
        prevWeek={prevWeek}
      />
    );
  }
  if (showWelcomeScreen)
    return (
      <WelcomeComponent
        moduleNumber={prevWeek}
        startAssessment={startAssessment}
      />
    );

  return (
    <>
      <Modal
        show={warning.state}
        onHide={() => setWarning({ state: false, message: "" })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{warning?.message || "An unknown error occurred."}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="warning"
            onClick={() => setWarning({ state: false, message: "" })}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Container>
        <Card>
          <Card.Title>Quiz for Week {`${prevWeek}`}</Card.Title>
        </Card>
        {questions &&
          questions.map((question, questionIdx) =>
            toBeAnswered && toBeAnswered.includes(questionIdx) ? (
              <div className="mt-2 mb-3">
                <Question
                  className="border border-danger p-3"
                  questionIdx={questionIdx}
                  totalQuestions={questions ? questions.length : 0}
                  questionText={question.questionTitle}
                  options={question.choices}
                  selections={
                    questionnaireChoices.filter(
                      (q) => q.number === questionIdx
                    )[0]["choice"]
                  }
                  isMultipleChoice={false}
                  saveSelection={saveSelection}
                />
              </div>
            ) : (
              <div className="mt-2 mb-3">
                <Question
                  className="border p-3"
                  questionIdx={questionIdx}
                  totalQuestions={questions ? questions.length : 0}
                  questionText={question.questionTitle}
                  options={question.choices}
                  selections={
                    questionnaireChoices.filter(
                      (q) => q.number === questionIdx
                    )[0]["choice"]
                  }
                  isMultipleChoice={false}
                  saveSelection={saveSelection}
                />
              </div>
            )
          )}
        <div
          style={{ display: "flex", justifyContent: "center" }}
          className="mb-2"
        >
          <Button primary onClick={submitQuestionnaire}>
            Submit Quiz
          </Button>
        </div>
      </Container>
    </>
  );
}
